import { Dispatch, SetStateAction, useState } from 'react';

import { useMutateAnalysisSchemaFiles } from '@hooks/configurationDataModelManagement/useMutateAnalysisSchemaFiles';
import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { uploadFileToGcloud } from '@api/common';
import { uploadOranSchema, uploadSchema } from '@api/schemaConfig';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IConfigurationDataAnalysisValues } from '@interfaces/cmModule/configurationData';
import { IAnalysisSchemaUrl } from '@interfaces/cmModule/schemaConfig';
import { IFilesFiles } from '@interfaces/common/file';
import { ICreateUploadUrl } from '@interfaces/common/handlers';

import { ESnackbarTypes } from '@enums/common';

import { HUNDRED_PERCENT_LOADING } from '@constants/common';
import { UPLOAD_URL_ATTACHMENT_SCHEMA } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface ISchemaUploadReturnType extends ICreateUploadUrl<(data: IFilesFiles) => void> {
	isUploadingFile: boolean;
}

interface ISchemaUploadProps {
	setImportIds: Dispatch<SetStateAction<string[]>>;
	setIsConfirmModals: Dispatch<SetStateAction<boolean>>;
	setAnalyzedConfigs: Dispatch<SetStateAction<IAnalysisSchemaUrl[]>>;
	formValues?: Partial<IConfigurationDataAnalysisValues>;
	setErrorAnalysisSchema: Dispatch<SetStateAction<string | undefined>>;
	isOranVendor: boolean;
}

export function useMutateSchemaUpload({
	setImportIds,
	setAnalyzedConfigs,
	formValues,
	setIsConfirmModals,
	setErrorAnalysisSchema,
	isOranVendor,
}: ISchemaUploadProps): ISchemaUploadReturnType {
	const [progress, setProgress] = useState<number>(HUNDRED_PERCENT_LOADING);
	const dispatch = useAppDispatch();
	const queryFn = isOranVendor ? uploadOranSchema : uploadSchema;

	const {
		getAnalysisSchema,
		isLoadingAnalysisSchema,
	} = useMutateAnalysisSchemaFiles({
		setAnalyzedConfigs,
		setIsConfirmModals,
		setErrorAnalysisSchema,
		isOranVendor,
	});

	const {
		mutate: createUploadUrlAttachment,
	} = useMutation<IFilesFiles>({
		mutationKey: [UPLOAD_URL_ATTACHMENT_SCHEMA, formValues],
		mutationFn: async ({ files }: IFilesFiles) => {
			const promises = files.map(async (file) => {
				try {
					const data = await queryFn(file.name);
					const url = data?.uploadUrl;

					setProgress(0);
					setImportIds((prev) => [...prev, data?.importId || '']);

					await uploadFileToGcloud({ url, data: file });

					if (data?.importId && formValues) {
						const preparedFormValues = Object.entries(formValues)
							.filter(([_key, value]) => !!value)
							.reduce((obj, [key, value]) =>
								({ ...obj, [key]: value }), {});

						getAnalysisSchema({
							...preparedFormValues,
							importId: data.importId,
							file,
						});
					}
				} catch (err: any) {
					handleErrorFromMessage(err);
					dispatch(setSnackbar({
						message: err?.message || responseMessages.error,
						type: ESnackbarTypes.error,
					}));
				}
			});

			await Promise.allSettled(promises);

			setProgress(HUNDRED_PERCENT_LOADING);
		},
	});

	return {
		createUploadUrlAttachment,
		isUploadingFile: (progress !== undefined ? progress !== HUNDRED_PERCENT_LOADING : false)
			|| isLoadingAnalysisSchema,
	};
}
